import { Injectable } from '@angular/core';
import { ApiGatewayService } from '@http/apiGateway.service';
import { INewsResponse, NewsViewModel, NEWS_TYPE } from '@models/news.class';
import { Pagination } from '@models/pagination';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { isEmpty } from 'lodash';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { AppState, HTTP_STATUS_CODE } from 'src/app';
import { SearchService } from 'src/core';
import { NotificationService } from 'src/core/services/notification.service';

@Injectable()
export class NewsService {
  private appConfiguration$: Observable<any>;
  private gbmNewsAPI: string;

  constructor(
    private notificationService: NotificationService,
    private searchService: SearchService,
    private store: Store<AppState>,
    private apiGatewayService: ApiGatewayService,
  ) {
    this.appConfiguration$ = this.store.select('config');

    this.appConfiguration$
      .pipe(
        filter((mapShadowed: any) => Map.isMap(mapShadowed)),
        map((mapShadowed: any) => mapShadowed.toJS()),
        take(1),
      )
      .subscribe((value) => {
        this.gbmNewsAPI = value.newsApi;
      });
  }

  getNewsByIssue(
    issueId: string,
    page: number,
    pageSize: number,
  ): Observable<Pagination<NewsViewModel>> {
    return this.apiGatewayService
      .get(
        `${this.gbmNewsAPI}v1/news/issues/${issueId}?page=${page}&page_size=${pageSize}`,
      )
      .pipe(
        map((news: any) => {
          const itemsTransformed = !isEmpty(news.items)
            ? news.items.map((item: INewsResponse) =>
                NewsViewModel.fromJSON(item, this.searchService.issues),
              )
            : [];
          news.items = itemsTransformed.filter((item: NewsViewModel) => {
            return item.type !== NEWS_TYPE.newsAdvances;
          });
          return news;
        }),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getNewsByCategory(
    category: string,
    page: number,
    pageSize: number,
    ticker?: string,
  ): Observable<Pagination<NewsViewModel>> {
    const tickerFilter = ticker ? `/issues/${ticker}` : '';

    return this.apiGatewayService
      .get(
        `${this.gbmNewsAPI}v1/news/category/${category}${tickerFilter}?page=${page}&page_size=${pageSize}`,
      )
      .pipe(
        map((news: any) => {
          const itemsTransformed = !isEmpty(news.items)
            ? news.items.map((item: INewsResponse) =>
                NewsViewModel.fromJSON(item, this.searchService.issues),
              )
            : [];
          news.items = itemsTransformed.filter((item: NewsViewModel) => {
            return item.type !== NEWS_TYPE.newsAdvances;
          });
          return news;
        }),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getNewsDetail(newsId: number): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmNewsAPI}v1/news/${newsId}`)
      .pipe(
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  private handleError(error: any) {
    this.notificationService.showServiceError(error);
    return observableThrowError(error);
  }
}
